import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import { Heart, MessageCircle, Trophy, Users, Flag } from "lucide-react";

interface NotificationItemProps {
  notification: Notification;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'match':
      return <Heart className="h-4 w-4 text-primary" />;
    case 'message':
      return <MessageCircle className="h-4 w-4 text-blue-500" />;
    case 'achievement':
      return <Trophy className="h-4 w-4 text-primary" />;
    case 'follow':
      return <Users className="h-4 w-4 text-green-500" />;
    case 'report':
      return <Flag className="h-4 w-4 text-red-500" />;
    default:
      return <Heart className="h-4 w-4 text-muted-foreground" />;
  }
};

export const NotificationItem = ({ notification }: NotificationItemProps) => {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();

  const handleClick = () => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate based on notification type and data
    if (notification.data?.path) {
      navigate(notification.data.path);
    } else {
      switch (notification.type) {
        case 'match':
          navigate('/runner-match');
          break;
        case 'message':
          if (notification.data?.roomId) {
            navigate(`/chat?room=${notification.data.roomId}`);
          } else {
            navigate('/chat');
          }
          break;
        case 'achievement':
          navigate('/profile');
          break;
        case 'follow':
          if (notification.data?.userId) {
            navigate(`/profile/${notification.data.userId}`);
          }
          break;
        default:
          break;
      }
    }
  };

  return (
    <div
      onClick={handleClick}
      data-testid="notification-item"
      className={cn(
        "p-4 hover:bg-accent/50 cursor-pointer transition-colors",
        !notification.read && "bg-accent/30"
      )}
    >
      <div className="flex gap-3">
        <div className="flex-shrink-0 mt-1">
          {getNotificationIcon(notification.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium line-clamp-1">
            {notification.title}
          </p>
          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
            {notification.message}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {formatDistanceToNow(new Date(notification.created_at), {
              addSuffix: true,
              locale: ptBR,
            })}
          </p>
        </div>
        {!notification.read && (
          <div className="flex-shrink-0">
            <div className="h-2 w-2 rounded-full bg-primary" />
          </div>
        )}
      </div>
    </div>
  );
};
